/* eslint-disable no-unused-vars */
import { uploadWorkspaceLogo } from '@/api/workspace';
import { COUNTRIES, COUNTRY_CODES } from '@/const/countries.constants';
import IMAGES from '@/const/images';
import { LOGOS } from '@/const/logos';
import {
    Alert,
    Button,
    Form,
    FormInstance,
    Input,
    Select,
    Space,
    Typography,
    notification,
} from 'antd';
import { get } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getRegionsApi } from '../../../api/countries.api';
import { IDefaultLogo } from '../../../interfaces/logo.interface';
import { startCreateWorkspace, useWorkspaceStore } from '../../../store/workspace.store';
import { Header } from '../Header';
import SelectLogoContent from '../SelectLogoContent';

interface IProps {
    t: TFunction;
}

interface IFormContainerProps extends IProps {
    form: FormInstance;
    error: string | null;
    loading: boolean;
    cities: string[];
    onSubmit: (values: any) => void;
    selectCities: (value: string) => void;
    setDefaultLogo: (value: string) => void;
}

const { Title, Text } = Typography;

const FormContainer: FC<IFormContainerProps> = ({
    t,
    form,
    error,
    loading,
    cities,
    onSubmit,
    selectCities,
    setDefaultLogo,
}) => (
    <Form form={form} onFinish={onSubmit} layout="vertical">
        {
            error && (
                <Alert
                    message={t(error)}
                    type="error"
                    style={{ marginBottom: 24 }}
                />
            )
        }

        <Form.Item
            name="name"
            label={t('CREATE_WORKSPACE.NAME_DELIVERY')}
            rules={[{ required: true }]}
        >
            <Input
                size="large"
                placeholder={t('CREATE_WORKSPACE.NAME_DELIVERY_PLACEHOLDER')}
            />
        </Form.Item>

        <Form.Item
            key="country"
            name="country"
            label={t('MEASURE_QUESTIONS.COUNTRY')}
            rules={[{ required: true, message: t('FIELD_REQUIRED') }]}
            initialValue={COUNTRIES[0].value}
        >
            <Select showSearch onChange={selectCities}>
                {
                    COUNTRIES.map(({ value, flag }: any) => (
                        <Select.Option
                            key={value}
                            value={value}
                        >
                            <Space size={8}>
                                <img src={flag} alt={t(`COUNTRY.${value}`)} />
                                {t(value)}
                            </Space>
                        </Select.Option>
                    ))
                }
            </Select>
        </Form.Item>

        <Form.Item
            key="coverageCities"
            name="coverageCities"
            label={t('CREATE_WORKSPACE.COVERAGE_CITIES')}
            rules={[{ required: true, message: t('FIELD_REQUIRED') }]}
        >
            <Select
                allowClear
                mode="multiple"
                placeholder={t('CREATE_WORKSPACE.COVERAGE_CITIES')}
            >
                {
                    cities.map((city: any) => (
                        <Select.Option
                            key={city}
                            value={city}
                        >
                            {city}
                        </Select.Option>
                    ))
                }
            </Select>
        </Form.Item>

        <SelectLogoContent
            showTitle={false}
            setDefaultLogo={setDefaultLogo}
        />

        <Button
            block
            type="primary"
            loading={loading}
            htmlType="submit"
            style={{ marginTop: 24 }}
        >
            {t('CREATE_WORKSPACE.FINALIZE')}
        </Button>
    </Form>
);

const CreateWorkspace = () => {
    const { t } = useTranslation('onboarding');

    const navigate = useNavigate();

    const [form] = Form.useForm();
    const [cities, setCities] = useState<string[]>([]);
    const [defaultLogo, setDefaultLogo] = useState<null | string>('');

    const workspace = useWorkspaceStore((state) => state.data);
    const error = useWorkspaceStore((state) => state.error);
    const setError = useWorkspaceStore((state) => state.setError);
    const loading = useWorkspaceStore((state) => state.loading);
    const setLoading = useWorkspaceStore((state) => state.setLoading);

    const selectCities = (value: string) => {
        if (value !== workspace?.country) form.setFieldValue('coverageCities', []);

        setCities([]);
        notification.destroy();

        getRegionsApi(value)
            .then(({ body }: any) => {
                const citiesSorted = body.cities.sort();
                setCities(citiesSorted);
            })
            .catch((error: any) => {
                const code = get(error, 'response.status', 500);
                const description = code === 404 ? t('RESPONSE_ACTIONS.COUNTRY_NOT_FOUND') : t('RESPONSE_ACTIONS.ERROR_OCCURRED');
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const onSubmit = async (values: any) => {
        let image: string = '';
        let color: string = '6C8EEF';
        setError(null);

        try {
            setLoading(true);

            if (!defaultLogo && !values.logo) {
                setError(t('CREATE_WORKSPACE.LOGO_IS_REQUIRED_DESCRIPTION'));
                return;
            }

            if (values.logo && values.logo.length > 0) {
                const uploadRes = await uploadWorkspaceLogo(values.logo[0].originFileObj);
                image = uploadRes.data.thumbnail;
                delete values.logo;
            }

            if (defaultLogo && !values.logo) {
                const defaultLogoFind: IDefaultLogo | undefined = LOGOS.driver.find((logo) => logo.type === defaultLogo);

                if (defaultLogoFind) {
                    image = defaultLogoFind.url;
                    color = defaultLogoFind.color;
                    delete values.logo;
                }
            }

            const payload: any = {
                ...values,
                image,
                color,
                countryCode: COUNTRY_CODES[values.country as keyof typeof COUNTRY_CODES],
            };

            await startCreateWorkspace(payload);
            navigate('/');
        } catch (error: any) {
            setError(error.response.data.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        selectCities(form.getFieldValue('country'));
    }, []);

    useEffect(() => {
        if (workspace) navigate('/');
    }, [workspace]);

    return (
        <div
            style={{ backgroundImage: `url(${IMAGES.login.spirals})` }}
            className="public-layout"
        >
            <div className="public-layout__container">
                <div className="public-layout__container--form">
                    <Space direction="vertical" size={40}>
                        <Header />
                        <Title
                            level={2}
                            style={{
                                color: '#6C8EEF',
                                marginBottom: 0,
                                textAlign: 'center',
                            }}
                        >
                            {t('CREATE_WORKSPACE.DELIVERY_INFORMATION')}
                        </Title>
                        <Space direction="vertical" size={24}>
                            <FormContainer
                                t={t}
                                form={form}
                                error={error}
                                loading={loading}
                                onSubmit={onSubmit}
                                cities={cities}
                                selectCities={selectCities}
                                setDefaultLogo={setDefaultLogo}
                            />
                        </Space>
                    </Space>
                </div>
            </div>
        </div>
    );
};

export { CreateWorkspace };
